import { useEffect, useState } from 'react';
import { SelectPeopleModal, type SelectPeopleSection } from 'src/design-system/SelectPeopleModal';
import { financialItemNoun } from 'src/financial/financial-data/financial-item/financialItemNoun';
import { FinancialCategory } from 'src/financial/financial-data/hooks/useFinancialItems';
import { FamilyMember } from 'src/non-financial-data/v2/family/useFamilyMembers';
import { formatName, NameFormat } from 'src/utils/nameFormatting';

type FamilyHolderModalProps = {
  opened: boolean;
  onClose: () => void;
  members: FamilyMember[];
  /** Chosen when the picker opens — the client the view adding the item is anchored on. */
  currentClientId: string;
  /** Names the thing being added in the title. Absent only while the picker is closed. */
  category?: FinancialCategory;
  onContinue: (clientId: string) => void;
};

/**
 * Asks which family member an item is being added for, where the item cannot record owners and
 * so hangs off whoever it is created against and nobody else. {@link FamilyOwnerModal} asks the
 * same question of an item that can be shared, and takes any number of answers; this one takes
 * exactly one, so it offers a radio per member rather than a checkbox.
 */
export const FamilyHolderModal = ({
  opened,
  onClose,
  members,
  currentClientId,
  category,
  onContinue,
}: FamilyHolderModalProps) => {
  const [selectedClientId, setSelectedClientId] = useState(currentClientId);

  // Opening starts again from the anchor, so a member chosen for one item is not still chosen
  // for the next one added from the same screen.
  useEffect(() => {
    if (!opened) return;
    setSelectedClientId(currentClientId);
  }, [opened, currentClientId]);

  const section: SelectPeopleSection = {
    key: 'holder',
    title: 'Family member',
    singleSelect: true,
    // Nobody can be added here: the item goes to one of the family, or it is not a family item.
    addLabel: '',
    searchPlaceholder: '',
    rows: members.map(member => ({
      id: member.clientId,
      name: formatName(member.client.firstName, member.client.lastName, NameFormat.NATURAL),
      role: member.roleLabel ?? '',
      checked: selectedClientId === member.clientId,
      onToggle: () => setSelectedClientId(member.clientId),
    })),
  };

  const itemNoun = financialItemNoun(category);

  return (
    <SelectPeopleModal
      opened={opened}
      onClose={onClose}
      title={`Who is this ${itemNoun} for?`}
      subtitle={`This ${itemNoun} cannot be shared, so it is recorded against one family member`}
      confirmLabel="Continue"
      confirmDisabled={selectedClientId === ''}
      sections={[section]}
      onConfirm={() => onContinue(selectedClientId)}
    />
  );
};
